import { useCallback, useEffect, useRef, useState } from 'react'

import type { AppointmentAction } from './appointment-actions-contracts'
import {
  appointmentActionError,
  type AppointmentActionsRepository,
} from './appointment-actions-repository'

export type AppointmentActionState =
  | { status: 'idle' }
  | { status: 'submitting'; action: AppointmentAction }
  /** The service re-read the appointment in the requested state. */
  | {
      status: 'succeeded'
      action: AppointmentAction
      already: boolean
      message: string
    }
  /** The change may or may not have happened: never claimed either way. */
  | { status: 'reconciliation'; action: AppointmentAction; message: string }
  /** Refused before anything changed. */
  | { status: 'failed'; action: AppointmentAction; message: string }

function unverified(action: AppointmentAction) {
  return action === 'confirm'
    ? 'Flio couldn’t verify whether this appointment was confirmed. Check your practice system before trying again.'
    : 'Flio couldn’t verify whether this appointment was cancelled. Check your practice system before trying again.'
}

function refusal(
  action: AppointmentAction,
  category: ReturnType<typeof appointmentActionError>
) {
  switch (category) {
    case 'forbidden':
      return 'You don’t have permission to change this appointment.'
    case 'not-found':
      return 'This appointment couldn’t be found. Nothing was changed.'
    case 'ineligible':
      return action === 'confirm'
        ? 'This appointment can’t be confirmed right now. Nothing was changed.'
        : 'This appointment can no longer be cancelled. Nothing was changed.'
    case 'rejected':
      return 'Your practice system didn’t accept the change. Nothing was changed.'
    case 'invalid':
      return 'The change couldn’t be sent. Nothing was changed.'
    default:
      return null
  }
}

export function useAppointmentAction(
  repository: AppointmentActionsRepository,
  tenantId: string,
  appointmentId: string | null
) {
  const [state, setState] = useState<AppointmentActionState>({ status: 'idle' })
  const generation = useRef(0)

  useEffect(() => {
    generation.current += 1
    setState({ status: 'idle' })
    return () => {
      generation.current += 1
    }
  }, [appointmentId, tenantId])

  const execute = useCallback(
    async (action: AppointmentAction) => {
      if (!appointmentId || state.status === 'submitting') return
      const requestGeneration = ++generation.current
      setState({ status: 'submitting', action })
      try {
        const result = await repository.execute(tenantId, appointmentId, action)
        if (generation.current !== requestGeneration) return
        if (
          result.appointmentId !== appointmentId ||
          result.outcome === 'reconciliation-required'
        ) {
          // An answer about another appointment proves nothing about this one.
          setState({
            status: 'reconciliation',
            action,
            message: unverified(action),
          })
          return
        }
        const already = result.outcome.startsWith('already-')
        setState({
          status: 'succeeded',
          action,
          already,
          message:
            action === 'confirm'
              ? already
                ? 'This appointment was already confirmed.'
                : 'Appointment confirmed.'
              : already
                ? 'This appointment was already cancelled.'
                : 'Appointment cancelled.',
        })
      } catch (error) {
        if (generation.current !== requestGeneration) return
        const message = refusal(action, appointmentActionError(error))
        setState(
          message
            ? { status: 'failed', action, message }
            : { status: 'reconciliation', action, message: unverified(action) }
        )
      }
    },
    [appointmentId, repository, state.status, tenantId]
  )

  return { state, execute }
}
